// A2 "Activity statistics" card (round 13 #2, option B — 04/Oct/26):
//   * pick what to count: Issues logged · Issues added · Reports published · Progress updates (greyed until that module is on)
//   * one line per period — 7 / 30 / 60 / 90 days — each a RUNNING TOTAL: on every day, how many in the N days up to it
//   * the number on each period chip = the total up to today; click a chip to hide / show its line
//   * date range picker: Last 30 days · Last 90 days (default) · Last 6 months · This year · Since project start · custom From / To
//   * hover the graph for one day's numbers; imported issues are not counted (app.activity.imported)
//   * click the card title to collapse / expand (remembered on this PC)
// Data: project_activity_series (daily counts incl. 89 lead-in days) → runningTotals() in core/lists.ts.
import { useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { useRpc } from "../api/useRpc";
import { DATE_HINT, formatDate } from "../format";
import { niceScale, presetRange, runningTotals, type RangePreset } from "../lists";
import { DateInput, ErrorBox, InfoIcon, Popover, Seg, useCollapsed } from "./kit";
import { Icon } from "./icons";

type Kind = "logged" | "added" | "published" | "progress";
const PERIODS = [7, 30, 60, 90] as const;
export const PERIOD_COLOUR: Record<number, string> = { 7: "#D85A30", 30: "#378ADD", 60: "#1D9E75", 90: "#7F77DD" };
const PRESETS: [RangePreset, string][] = [["30", "Last 30 days"], ["90", "Last 90 days"], ["180", "Last 6 months"], ["year", "This year"], ["start", "Since project start"]];
export const ACTIVITY_STATS_TIP = [
  "Pick what to count: issues logged, issues added, reports published or progress updates.",
  "Each line is a running total: on every day, how many in the 7, 30, 60 or 90 days up to that day.",
  "The number on each period chip is the total up to today.",
  "Date range sets the days shown (default: last 90 days).",
  "Hover for the numbers on a day; click a period chip to hide or show its line.",
  "Imported issues are not counted.",
  "Progress updates appear once Progress reporting is switched on.",
  "Click the card title to collapse or expand it; remembered on this PC.",
];

export function ActivityStats({ code, today, progressOn }: { code: string; today: string; progressOn: boolean }) {
  const [collapsed, toggle] = useCollapsed("activity-stats");
  const [kind, setKind] = useState<Kind>("logged");
  const [preset, setPreset] = useState<RangePreset | "custom">("90");
  const [range, setRange] = useState(() => presetRange("90", today, null));
  const [hidden, setHidden] = useState<number[]>([]);
  const { data, error } = useRpc("project_activity_series", { code, kind, from: range.from, to: range.to });
  return (
    <section className={`card pad act-stats${collapsed ? " collapsed" : ""}`} data-testid="activity-stats">
      <div className="row between card-head">
        <h2 className="row gap6">
          <button type="button" className="collapse-head" aria-expanded={!collapsed} onClick={toggle}>
            <span className="chev"><Icon name={collapsed ? "chevron_right" : "expand_more"} /></span>Activity statistics</button>
          <InfoIcon tip={ACTIVITY_STATS_TIP} />
        </h2>
        {!collapsed && <Seg label="Count" value={kind} onChange={setKind} options={[
          { value: "logged", label: "Issues logged" }, { value: "added", label: "Issues added" }, { value: "published", label: "Reports published" },
          { value: "progress", label: "Progress updates", disabled: !progressOn, title: progressOn ? undefined : "Shown once Progress reporting is switched on" }]} />}
      </div>
      {!collapsed && <>
        <div className="row gap6 legend-row">
          {PERIODS.map((p) => (
            <button key={p} type="button" className={`legend-chip${hidden.includes(p) ? " off" : ""}`} aria-pressed={!hidden.includes(p)} data-period={p}
              onClick={() => setHidden(hidden.includes(p) ? hidden.filter((x) => x !== p) : [...hidden, p])}>
              <i style={{ background: PERIOD_COLOUR[p] }} />{p === 7 ? "7 days" : `${p} days`}<b>{data ? data.totals[String(p)] ?? 0 : "–"}</b>
            </button>))}
          <span className="grow" />
          <RangePicker today={today} first={data?.first ?? null} preset={preset} range={range}
            onPick={(p, r) => { setPreset(p); setRange(r); }} />
        </div>
        {error && <ErrorBox message={error} />}
        {data && <LineChart days={data.days} from={data.from} hidden={hidden} />}
        <p className="muted tiny row gap6">Imported issues are not counted <InfoIcon tip={["Issues and log entries brought in from an old report (e.g. Clash Report No 1) are marked Imported.", "They show in the issue list as usual but are left out of activity statistics, so the graph shows work done in the app."]} /></p>
      </>}
    </section>
  );
}

function RangePicker({ today, first, preset, range, onPick }: {
  today: string; first: string | null; preset: RangePreset | "custom"; range: { from: string; to: string };
  onPick: (p: RangePreset | "custom", r: { from: string; to: string }) => void;
}) {
  const [from, setFrom] = useState<string | null>(range.from);
  const [to, setTo] = useState<string | null>(range.to);
  const [bad, setBad] = useState({ from: false, to: false });
  const [msg, setMsg] = useState<string | null>(null);
  return (
    <Popover label="Date range" align="right" className="range-pop"
      button={() => <span className="range-btn" data-testid="date-range"><Icon name="calendar_today" /> {formatDate(range.from)} – {formatDate(range.to)} <Icon name="arrow_drop_down" /></span>}>
      {(close) => (
        <div className="range-menu">
          <div className="presets">
            {PRESETS.map(([p, label]) => (
              <button key={p} type="button" className={`menu-item${preset === p ? " current" : ""}`}
                onClick={() => { const r = presetRange(p, today, first); setFrom(r.from); setTo(r.to); onPick(p, r); close(); }}>
                {label}{preset === p && <> <Icon name="check" /></>}</button>))}
          </div>
          <div className="custom">
            <p className="muted small">Custom range</p>
            <span className="tiny muted">From</span>
            <DateInput ariaLabel="Range from" value={from} onChange={setFrom} onValidity={(ok) => setBad((b) => ({ ...b, from: !ok }))} />
            <span className="tiny muted">To</span>
            <DateInput ariaLabel="Range to" value={to} onChange={setTo} onValidity={(ok) => setBad((b) => ({ ...b, to: !ok }))} />
            {msg && <p className="error-text small">{msg}</p>}
            <div className="row end"><button type="button" className="btn primary" onClick={() => {
              const f = from, t = to;
              if (bad.from || bad.to || !f || !t) return setMsg(DATE_HINT);
              if (f > t) return setMsg("From must be before To");
              setMsg(null); onPick("custom", { from: f, to: t }); close();
            }}>Apply</button></div>
          </div>
        </div>)}
    </Popover>
  );
}

const H = 156, L = 30, R = 34, T = 8, B = 20;
const addDays = (iso: string, n: number) => new Date(new Date(`${iso}T00:00:00Z`).getTime() + n * 86400000).toISOString().slice(0, 10);

/** SVG line chart of the running totals; hover shows the day's four numbers. */
function LineChart({ days, from, hidden }: { days: number[]; from: string; hidden: number[] }) {
  const box = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(640);                         // drawn at the card's real width so text never stretches
  useEffect(() => {
    const el = box.current; if (!el) return;
    const ro = new ResizeObserver(() => setW(Math.max(320, Math.round(el.clientWidth))));
    ro.observe(el); setW(Math.max(320, Math.round(el.clientWidth)));
    return () => ro.disconnect();
  }, []);
  const series = useMemo(() => PERIODS.map((p) => ({ p, v: runningTotals(days, p) })), [days]);
  const n = series[0].v.length;
  const shown = series.filter((s) => !hidden.includes(s.p));
  const { top, step } = niceScale(Math.max(1, ...shown.flatMap((s) => s.v)));
  const pw = W - L - R, ph = H - T - B;
  const X = (i: number) => L + (n <= 1 ? pw : (pw * i) / (n - 1));
  const Y = (v: number) => T + ph * (1 - v / top);
  const [hover, setHover] = useState<number | null>(null);
  const svg = useRef<SVGSVGElement>(null);
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(f * (n - 1)));
  const onMove = (e: MouseEvent) => {
    const box = svg.current?.getBoundingClientRect(); if (!box) return;
    const x = ((e.clientX - box.left) / box.width) * W;
    setHover(Math.min(n - 1, Math.max(0, Math.round(((x - L) / pw) * (n - 1)))));
  };
  return (
    <div className="chart" ref={box} data-testid="activity-chart" data-points={n}>
      <svg ref={svg} viewBox={`0 0 ${W} ${H}`} width={W} height={H} onMouseMove={onMove} onMouseLeave={() => setHover(null)} role="img" aria-label="Activity running totals">
        {Array.from({ length: top / step + 1 }, (_, k) => k * step).map((v) => <g key={v}>
          <line x1={L} x2={W - R} y1={Y(v)} y2={Y(v)} stroke="#ece9e1" />
          <text x={L - 6} y={Y(v) + 3.5} textAnchor="end" className="ax">{v}</text></g>)}
        {ticks.map((t, k) => <text key={k} x={X(t)} y={H - 5} textAnchor={k === 0 ? "start" : k === 4 ? "end" : "middle"} className="ax">{formatDate(addDays(from, t)).slice(0, 6)}</text>)}
        {shown.map(({ p, v }) => <g key={p} data-line={p}>
          <path d={v.map((y, i) => `${i ? "L" : "M"}${X(i).toFixed(1)},${Y(y).toFixed(1)}`).join(" ")} fill="none" stroke={PERIOD_COLOUR[p]} strokeWidth="2" strokeLinejoin="round" />
          <circle cx={X(n - 1)} cy={Y(v[n - 1])} r="3" fill={PERIOD_COLOUR[p]} />
          <text x={X(n - 1) + 6} y={Y(v[n - 1]) + 3.5} className="end-label" fill={PERIOD_COLOUR[p]}>{v[n - 1]}</text></g>)}
        {hover !== null && <g><line x1={X(hover)} x2={X(hover)} y1={T} y2={T + ph} stroke="#8f8e88" strokeDasharray="2 2" />
          {shown.map(({ p, v }) => <circle key={p} cx={X(hover)} cy={Y(v[hover])} r="3.5" fill="#fff" stroke={PERIOD_COLOUR[p]} strokeWidth="2" />)}</g>}
      </svg>
      {hover !== null && (
        <div className="chart-tip" role="tooltip" style={{ left: `${(X(hover) / W) * 100}%` }} data-testid="chart-tip">
          <b>{formatDate(addDays(from, hover))}</b>
          {shown.map(({ p, v }) => <div key={p}><i style={{ background: PERIOD_COLOUR[p] }} />{p} days: <b>{v[hover]}</b></div>)}
        </div>)}
    </div>
  );
}
